/**
 * Blog post: ÖSD Zertifikat B2 – Österreichische Nuancen, Hörverstehen & Redemittel.
 * Content: Austrian nuances, Two Modules, Redemittel link, Prüfungstraining.
 */
import React from 'react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function OsdB2ExamGuidePost() {
  const lang = useLangParam();
  const blogBase = `/${lang}/blog`;

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        <strong>Meistere die B2 Prüfung mit Fokus auf österreichische Nuancen – Präsentiert von logami.app.</strong>
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        Das <strong>ÖSD Zertifikat B2</strong> ist weit mehr als ein gewöhnlicher Sprachtest. Während andere Prüfungen oft nur Standarddeutsch aus Deutschland fokussieren, prüft das <strong>ÖSD Zertifikat</strong> deine Fähigkeit, Deutsch in seiner gesamten plurizentrischen Breite zu verstehen. Das bedeutet: In der <strong>B2 Prüfung</strong> triffst du auf authentische Texte aus Österreich, Deutschland und der Schweiz.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Focus: Austrian Nuances &amp; Listening
      </h2>
      <div className="my-6 p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
          Die größte Besonderheit beim <strong>ÖSD B2</strong> ist das <strong>Listening &amp; Writing</strong>-Modul. Hier begegnen dir im Hörverstehen oft österreichische Varietäten. Um hier zu punkten, musst du:
        </p>
        <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-black/70 leading-relaxed mb-4 ml-2">
          <li>Spezifisches Vokabular (Austriazismen) erkennen.</li>
          <li>Unterschiedliche Akzente im <strong>Listening</strong>-, <strong>Writing</strong>- und <strong>Speaking</strong>-Teil sicher zuordnen können.</li>
          <li>Die <strong>Reading</strong>-, <strong>Listening</strong>- und <strong>Writing</strong>-Aufgaben unter realistischen Zeitbedingungen trainieren.</li>
        </ul>
        <p className="text-base md:text-lg text-black/70 leading-relaxed">
          Nutze unsere <strong>Audio Files</strong> und <strong>PDF-Audio</strong>-Materialien, um dein Gehör schrittweise an diese Nuancen zu gewöhnen.
        </p>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Die Struktur: Two-Modules-System
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Die <strong>ÖSD Exams</strong> auf B2-Niveau bestehen aus <strong>two modules</strong>: dem Schriftlichen (Lesen, Hören, Schreiben) und dem Mündlichen (Sprechen). Du kannst diese <strong>zwei Module</strong> – schriftlich und mündlich – separat ablegen oder gemeinsam als <strong>Written Exam</strong> plus mündliche Prüfung absolvieren.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mb-10">
        <div className="p-4 rounded-2xl border-2 border-black/10 bg-white">
          <p className="font-space font-bold text-black mb-2">Written Module</p>
          <p className="text-sm text-black/70 leading-relaxed">Umfasst <strong>Reading</strong>, <strong>Listening</strong> und <strong>Writing</strong>.</p>
        </div>
        <div className="p-4 rounded-2xl border-2 border-black/10 bg-white">
          <p className="font-space font-bold text-black mb-2">Oral Module</p>
          <p className="text-sm text-black/70 leading-relaxed">Fokus auf <strong>Speaking</strong> und spontanes Reagieren.</p>
        </div>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Perfekte Vorbereitung mit Redemittel &amp; Musterlösungen
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Um im <strong>Writing</strong>- und <strong>Speaking</strong>-Teil zu glänzen, benötigst du Sicherheit. Ein <strong>Sample Test</strong> reicht oft nicht aus. Du brauchst eine klare Struktur für deinen <strong>Projekt ÖSD B2</strong>-Aufsatz.
      </p>
      <div className="my-10 p-6 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
        <h3 className="font-space text-lg font-bold text-black mb-3">Kostenlose ÖSD B2 Redemittel &amp; Strategien</h3>
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
          Sichere dir jetzt unsere <strong>B2 PDF</strong>-Sammlung mit exklusiven <strong>ÖSD B2 Lösungen</strong> und Redemitteln! Inklusive <strong>Modellprüfung ÖSD</strong>-Strategien für deinen Erfolg.
        </p>
        <Link
          to={`${blogBase}/osd-b2-redemittel-schreiben`}
          className="inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-black hover:bg-black/90 text-white font-space font-bold rounded-xl border-2 border-black shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Jetzt ÖSD B2 Redemittel (Free PDF) sichern
        </Link>
        <p className="mt-3 text-sm text-black/60">
          Hole dir das <strong>ÖSD B2 PDF</strong> mit <strong>Audio Files</strong> und starte dein <strong>Exam Reading</strong>-Training.
        </p>
      </div>
      <SprechenSamplesCallout provider="osd" level="b2" />

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Realistisches Prüfungstraining
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        Erfolg bei der <strong>ÖSD B2 Prüfung</strong> kommt durch Wiederholung. Nutze einen <strong>Sample Test ÖSD</strong>, um die <strong>Conditions</strong> der echten <strong>Examination</strong> zu simulieren.
      </p>
      <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-black/70 leading-relaxed mb-10 ml-2">
        <li>Trainiere mit <strong>Model Tests</strong> und <strong>Modellprüfung ÖSD Zertifikat</strong>-Materialien.</li>
        <li>Nutze unsere <strong>B2 Lösungen</strong>, um aus deinen Fehlern zu lernen.</li>
        <li>Lade dir die <strong>Fertigkeiten Lesen &amp; Hören</strong>-Übungen als <strong>Download</strong> auf dein Smartphone.</li>
      </ul>

      <div className="mt-10 p-6 rounded-2xl border-2 border-black/10 bg-[#1e293b] text-white">
        <h2 className="font-space text-xl font-bold text-white mb-3">
          Bereit für dein ÖSD B2 Zertifikat?
        </h2>
        <p className="text-base md:text-lg text-white/90 leading-relaxed mb-4">
          Egal ob <strong>Arena ÖSD B2</strong> oder <strong>Projekt ÖSD</strong> – wir begleiten dich mit dem besten <strong>B2 Exam Model</strong>. Starte dein Training kostenlos.
        </p>
        <Link
          to={`/${lang}/dashboard`}
          className="inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-space font-bold rounded-xl border-2 border-emerald-500 shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Jetzt Mock Examination starten
        </Link>
      </div>

      <p className="mt-10 pt-6 border-t border-black/10 text-sm text-black/50">
        Keywords: ÖSD B2, Zertifikat B2, Lösungen, Sample Test, Listening Writing, Austrian Nuances, Audio Files, Modellprüfung.
      </p>
      <RelatedResources provider="osd" level="b2" variant="exam-guide" />

    </article>
  );
}
